import React from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../services/supabase';
import type { EventOrderFormField } from '../../hooks/useEvents';
import { getPublicSiteBaseUrl } from '../../utils/publicSiteUrls';

type WaiverSubmissionRow = {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  created_at: string;
  form_response: unknown;
};

const slugifyEventTitle = (title: string) =>
  title
    .toLowerCase()
    .trim()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const formatWaiverAnswer = (value: unknown) => {
  if (value === true) return 'Yes';
  if (value === false || value == null) return '';
  if (Array.isArray(value)) return value.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
  return String(value).trim();
};

const readCollectPerAttendee = (field: Pick<EventOrderFormField, 'validation'>) => {
  if (!field.validation || typeof field.validation !== 'object' || Array.isArray(field.validation)) return false;
  return Boolean((field.validation as Record<string, unknown>).collect_per_attendee);
};

const waiverSubmissionAnswers = (row: WaiverSubmissionRow, fields: EventOrderFormField[]) => {
  const response = row.form_response && typeof row.form_response === 'object' && !Array.isArray(row.form_response)
    ? row.form_response as Record<string, unknown>
    : {};
  const attendeeRows = Array.isArray(response.attendee_responses)
    ? response.attendee_responses as Record<string, unknown>[]
    : [];
  const firstAttendee = attendeeRows[0]?.responses;
  const attendeeResponses = firstAttendee && typeof firstAttendee === 'object' && !Array.isArray(firstAttendee)
    ? firstAttendee as Record<string, unknown>
    : {};
  return fields
    .filter((field) => field.field_group === 'waiver')
    .slice()
    .sort((left, right) => left.sort_order - right.sort_order)
    .flatMap((field) => {
      const raw = readCollectPerAttendee(field) ? attendeeResponses[field.field_key] : response[field.field_key];
      const text = formatWaiverAnswer(raw);
      return text ? [{ label: field.label, text }] : [];
    });
};

const EventWaiverList: React.FC<{
  eventId?: string;
  eventTitle?: string | null;
  enabled: boolean;
  fields: EventOrderFormField[];
  canConfigure?: boolean;
}> = ({ eventId, eventTitle, enabled, fields, canConfigure = true }) => {
  const [copied, setCopied] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const slug = eventTitle ? slugifyEventTitle(eventTitle) : '';
  const waiverUrl = eventId
    ? `${getPublicSiteBaseUrl()}/events/${slug || eventId}/waiver`
    : '';
  const submissionsQuery = useQuery({
    queryKey: ['event-waiver-submissions', eventId],
    enabled: Boolean(eventId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_waiver_submissions')
        .select('id, full_name, email, phone, created_at, form_response')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data || []) as WaiverSubmissionRow[];
    },
  });
  const submissions = React.useMemo(
    () => submissionsQuery.data ?? [],
    [submissionsQuery.data],
  );
  const submissionRows = React.useMemo(
    () => submissions.map((row) => ({ row, answers: waiverSubmissionAnswers(row, fields) })),
    [fields, submissions],
  );
  const searchQuery = search.trim().toLowerCase();
  const visibleRows = React.useMemo(() => {
    if (!searchQuery) return submissionRows;
    const digits = searchQuery.replace(/\D/g, '');
    return submissionRows.filter(({ row, answers }) => {
      const text = [
        row.full_name,
        row.email,
        row.phone,
        ...answers.flatMap((answer) => [answer.label, answer.text]),
      ].join(' ').toLowerCase();
      if (text.includes(searchQuery)) return true;
      return Boolean(digits) && String(row.phone || '').replace(/\D/g, '').includes(digits);
    });
  }, [searchQuery, submissionRows]);
  const listUnavailable = /event_waiver_submissions|schema cache|does not exist/i.test(
    submissionsQuery.error instanceof Error ? submissionsQuery.error.message : '',
  );

  return (
    <div>
      <Hint>
        {enabled
          ? 'Share this link. People enter a name, phone, and email if they have one. Each submission is added to the list below.'
          : canConfigure
            ? 'Turn the waiver on from the Waiver tab to collect new submissions.'
            : 'This event is not collecting new waiver submissions.'}
      </Hint>
      {waiverUrl ? (
        <Field>
          <label>Waiver link</label>
          <input value={waiverUrl} readOnly />
          <MiniButton
            type="button"
            onClick={() => {
              void navigator.clipboard?.writeText(waiverUrl).then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              });
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </MiniButton>
        </Field>
      ) : null}
      {submissionsQuery.isLoading ? <Hint>Loading waiver list…</Hint> : null}
      {submissionsQuery.error ? (
        <ErrorText>
          {listUnavailable ? 'The waiver list is not available until the latest database migration is applied.' : 'Could not load the waiver list.'}
        </ErrorText>
      ) : null}
      {!submissionsQuery.isLoading && !submissionsQuery.error && submissions.length === 0 ? (
        <Hint>No waiver submissions yet.</Hint>
      ) : null}
      {submissions.length > 0 ? (
        <Field>
          <label htmlFor="waiver-list-search">Search</label>
          <input
            id="waiver-list-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, email, or phone"
          />
        </Field>
      ) : null}
      {submissions.length > 0 && visibleRows.length === 0 ? (
        <Hint>No waivers match that search.</Hint>
      ) : null}
      {visibleRows.length > 0 ? (
        <WaiverList>
          <WaiverTable>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(({ row, answers }) => {
                const submitted = row.created_at ? new Date(row.created_at).toLocaleString() : '';
                return (
                  <tr key={row.id}>
                    <td>{row.full_name || 'Unnamed'}</td>
                    <td>{row.email || '—'}</td>
                    <td>{row.phone || '—'}</td>
                    <td>
                      {submitted}
                      {answers.length > 0 ? (
                        <WaiverAnswers>
                          {answers.map((answer) => (
                            <div key={answer.label}>{answer.label}: {answer.text}</div>
                          ))}
                        </WaiverAnswers>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </WaiverTable>
        </WaiverList>
      ) : null}
    </div>
  );
};

export default EventWaiverList;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-bottom: 0.75rem;

  label {
    font-size: 0.74rem;
    font-weight: 600;
    color: var(--onSurfaceVariant);
    letter-spacing: 0.01em;
  }

  input {
    border: 1px solid var(--outline);
    border-radius: 10px;
    padding: 0.42rem 0.58rem;
    font-size: 0.84rem;
    background: var(--surface);
    color: var(--onSurface);
  }
`;

const MiniButton = styled.button`
  align-self: flex-start;
  margin-top: 0.35rem;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.2rem 0.55rem;
  cursor: pointer;
`;

const Hint = styled.p`
  font-size: 0.84rem;
  color: var(--onSurfaceVariant);
  margin: 0.25rem 0 0.65rem;
  line-height: 1.35;
`;

const ErrorText = styled.p`
  margin: 0.25rem 0;
  color: var(--error);
  font-weight: 600;
`;

const WaiverList = styled.div`
  overflow-x: auto;
  margin-top: 0.75rem;
`;

const WaiverTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;

  th,
  td {
    text-align: left;
    padding: 0.45rem 0.55rem;
    border-bottom: 1px solid var(--outline);
    vertical-align: top;
  }

  th {
    color: var(--onSurfaceVariant);
    font-weight: 700;
  }
`;

const WaiverAnswers = styled.div`
  margin-top: 0.35rem;
  color: var(--onSurfaceVariant);
  font-size: 0.75rem;
  line-height: 1.35;
`;
